import React, { useEffect, useRef, useState } from 'react';
import SelectCartItem from '../select-cart-item/select-cart-item';

import './add-edit-cart.scss';
import { useDispatch, useSelector } from 'react-redux';
import { addCartItem, deleteCartItem, editCartItem, editCartMediaType } from '../../../../actions/cashActions';
import { currencyFormat } from '../../../../utilities/core-utilities';
import isEmpty from 'utilities/isEmpty';
import { trackCustomEvent } from 'utilities/app-insights';
import MobileOrderSummary from '../mobile-order-summary/mobile-order-summary';

const AddEditCart = ({ denominationList, defaultPlasticTypeCode, preloaded, defaultExpiryDate, onValidStateChange, allowedMedia, fleetLimitters }) => {
  const dispatch = useDispatch();

  const itemsEndRef = useRef(null);

  const cartItems = useSelector((state) => state.cashStore.cashCartItems);
  const cartSubTotal = useSelector((state) => state.cashStore.cashCartSubTotal);
  const plasticTypesList = useSelector((state) => state.maintenanceStore.plasticTypesList);
  const isAdmin = useSelector((state) => state.userStore.isAmpolAdmin);
  const isDigitalCart = useSelector((state) => state.cashStore.isDigitalCartOnly);
  const loggedInUser = useSelector((state) => state.userStore.loggedInUser);
  const assignedAccountNumber = useSelector((state) => state.userStore.assignedAccountNumber);

  const [invalidCartItems, setInvalidCartItems] = useState([]);

  useEffect(() => {
    itemsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [cartItems]);

  const handleAddItem = (cartItem) => {
    dispatch(addCartItem(cartItem));
    trackCustomEvent('Add Card to Cart', loggedInUser, assignedAccountNumber, 'User selected card(s) with respective denominations and added them to the cart.');
    window.dataLayer.push({
      event: 'CP_Cash_Order_Add-Card',
      button_click: true,
      card_type: cartItem.mediaType === 'P' ? 'Physical' : 'Digital',
      denomination: cartItem.denominationCode,
      quantity: cartItem.quantity,
      extras: cartItem.extras.toString(),
    });
  };

  const handleEditItem = (cartItem) => {
    dispatch(editCartItem(cartItem));
  };

  const handleEditMediaType = (mediaType) => {
    dispatch(editCartMediaType(mediaType));
  };

  const handleRemoveItem = (cartItem) => {
    dispatch(deleteCartItem(cartItem.uuid));
    setInvalidCartItems(invalidCartItems.filter((id) => id !== cartItem.uuid));
    window.dataLayer.push({
      event: 'CP_Cash_Order_Remove_Card',
      button_click: true,
      card_type: cartItem.mediaType === 'P' ? 'Physical' : 'Digital',
      denomination: cartItem.denominationCode,
      quantity: cartItem.quantity,
      extras: cartItem.extras.toString(),
    });
    trackCustomEvent('Remove Card from Cart', loggedInUser, assignedAccountNumber, 'User removed card(s) from the cart.');
  };

  const handleEditStateChange = (cartItemUuid, isValid) => {
    if (isValid) {
      setInvalidCartItems(invalidCartItems.filter((id) => id !== cartItemUuid));
    } else if (!isValid && invalidCartItems.find((id) => id === cartItemUuid) === undefined) {
      setInvalidCartItems([...invalidCartItems, cartItemUuid]);
    }
  };
  
  useEffect(() => {
    onValidStateChange(invalidCartItems.length === 0);
  }, [invalidCartItems]);

  return <div className={'cart'} style={{ '--cart-exp-column-width': isAdmin && preloaded ? '112px 1fr' : '1fr' }}>
    <MobileOrderSummary 
      cashOrderCartItems={cartItems}
      cashCartSubTotal={cartSubTotal}
      onRemove={handleRemoveItem}
    />
    <div className="cart__order-summary">
      <div className={'cart__row cart__row--header'}>
        <div>Card Type</div>
        <div>Denomination</div>
        <div>Quantity</div>
        <div>Extras</div>
        {isAdmin && preloaded && <div>Expiry</div>}
      </div>
      <div className="cart__items">
        {Object.entries(cartItems)?.map(([key, value]) => (
          <SelectCartItem
            isAdmin={isAdmin}
            denominationList={denominationList}
            preloaded={preloaded}
            cartItem={value}
            key={key}
            onEdit={handleEditItem}
            onEditMediaType={handleEditMediaType}
            onRemove={handleRemoveItem}
            plasticTypesList={plasticTypesList}
            onEditStateChange={handleEditStateChange}
            allowedMedia={allowedMedia}
            fleetLimitters={fleetLimitters}
          />))}
        {Object.keys(cartItems).length === 0 && <div className="cart__empty">Add items to your cart to start an order.</div>}
        <div ref={itemsEndRef}></div>
      </div>
      <div className="cart__row cart__row--total">
        <div>Sub Total:</div>
        <div>{currencyFormat(cartSubTotal)}</div>
      </div>
    </div>
    <div className={'cart__add'}>
      <SelectCartItem
        isAdmin={isAdmin}
        denominationList={denominationList}
        defaultPlasticTypeCode={defaultPlasticTypeCode}
        preloaded={preloaded}
        onAdd={handleAddItem}
        plasticTypesList={plasticTypesList}
        defaultExpiryDate={defaultExpiryDate}
        defaultMediaType={!isEmpty(cartItems) ? (isDigitalCart ? 'D' : 'P') : null}
        allowedMedia={allowedMedia}
        fleetLimitters={fleetLimitters}
      />
    </div>
  </div>;
};

export default AddEditCart;
